import { Plus, Trash2 } from "lucide-react";
import { Input } from "./Input";
import { Button } from "./Button";

export type SlotType = "string" | "phone" | "email" | "date" | "number" | "enum";
export type PrivacyClass = "non_pii" | "pii" | "sensitive_pii";

export interface SlotDraft {
  name: string;
  label: string;
  type: SlotType;
  required: boolean;
  requiredGroup?: string;
  extractionHint: string;
  privacyClass: PrivacyClass;
  validationRegex?: string;
  normalizer?: string;
  enumValues?: string[];
}

export const EMPTY_SLOT: SlotDraft = {
  name: "",
  label: "",
  type: "string",
  required: true,
  requiredGroup: undefined,
  extractionHint: "",
  privacyClass: "non_pii",
};

const TYPE_OPTIONS: Array<{ value: SlotType; label: string }> = [
  { value: "string", label: "Text" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
  { value: "enum", label: "Enum (one of fixed values)" },
];

const PRIVACY_OPTIONS: Array<{ value: PrivacyClass; label: string; hint: string }> = [
  { value: "non_pii", label: "Non-PII", hint: "Order ID, ticket number, SKU — logged in plaintext." },
  { value: "pii", label: "PII", hint: "Name, phone, email, address — redacted in audit logs, masked in UI." },
  { value: "sensitive_pii", label: "Sensitive PII", hint: "DOB, govt ID, medical, financial — redacted everywhere by default." },
];

const NORMALIZERS: Array<{ value: string; label: string }> = [
  { value: "", label: "(none)" },
  { value: "lower_trim", label: "lower_trim" },
  { value: "upper_trim", label: "upper_trim" },
  { value: "phone_in", label: "phone_in (Indian +91)" },
  { value: "phone_us", label: "phone_us" },
  { value: "phone_e164", label: "phone_e164 (international)" },
  { value: "email_lower_trim", label: "email_lower_trim" },
  { value: "iso_date", label: "iso_date" },
  { value: "digits_only", label: "digits_only" },
];

interface Props {
  slots: SlotDraft[];
  onChange: (slots: SlotDraft[]) => void;
  /** Optional inline error message from the server (e.g. duplicate slot name). */
  error?: string | null;
}

/**
 * Editable list of slot definitions. Used by both Create and Edit Category.
 *
 * Slots tell the AI what info each category needs. The classifier extracts
 * matching values from inbound email; the gather step asks the customer
 * for missing required slots.
 */
export function SlotsEditor({ slots, onChange, error }: Props) {
  const update = (i: number, patch: Partial<SlotDraft>) =>
    onChange(slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const remove = (i: number) => onChange(slots.filter((_, idx) => idx !== i));

  const add = () => onChange([...slots, { ...EMPTY_SLOT }]);

  const normalizeName = (raw: string): string =>
    raw.toLowerCase().replace(/[^a-z0-9_]/g, "_").slice(0, 32);

  return (
    <div className="space-y-3">
      {slots.length === 0 && (
        <p className="text-sm text-gray-500">
          No slots — replies are generated from email content + knowledge alone. Add a slot when
          this category needs specific info from the customer (order ID, account email, etc.).
        </p>
      )}

      {slots.map((slot, i) => (
        <div key={i} className="space-y-3 rounded-md border border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Slot {i + 1}
            </span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-gray-400 hover:text-rose-600"
              aria-label="Remove slot"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Name"
              placeholder="order_id"
              value={slot.name}
              onChange={(e) => update(i, { name: normalizeName(e.target.value) })}
              hint="snake_case identifier used in templates"
            />
            <Input
              label="Customer-facing label"
              placeholder="your order ID (starts with MHL-)"
              value={slot.label}
              onChange={(e) => update(i, { label: e.target.value })}
              hint="What the gather step asks for"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-900">Type</label>
              <select
                value={slot.type}
                onChange={(e) => update(i, { type: e.target.value as SlotType })}
                className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900">Privacy</label>
              <select
                value={slot.privacyClass}
                onChange={(e) => update(i, { privacyClass: e.target.value as PrivacyClass })}
                className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                {PRIVACY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {PRIVACY_OPTIONS.find((o) => o.value === slot.privacyClass)?.hint}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900">Extraction hint</label>
            <textarea
              rows={2}
              maxLength={400}
              value={slot.extractionHint}
              onChange={(e) => update(i, { extractionHint: e.target.value })}
              placeholder="Order IDs look like MHL-447821 (six digits, prefixed with MHL-). Customers sometimes write just the digits."
              className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            <p className="mt-1 text-xs text-gray-500">
              Guidance for the AI when extracting from email. Specific examples + "do not extract X if Y" guidance produces clean results.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={slot.required}
                onChange={(e) => {
                  const req = e.target.checked;
                  update(i, { required: req, requiredGroup: req ? slot.requiredGroup : undefined });
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              Required (gather step asks customer if missing)
            </label>
            {slot.required && (
              <div className="mt-1 max-w-md">
                <Input
                  label="Required Group / Either-Or Group (optional)"
                  placeholder="e.g. contact_or_id"
                  value={slot.requiredGroup ?? ""}
                  onChange={(e) => update(i, { requiredGroup: e.target.value ? normalizeName(e.target.value) : undefined })}
                  hint="If multiple slots share the same required group name, gathering is complete once any one of them is filled."
                />
              </div>
            )}
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
              Advanced — validation, normalizer{slot.type === "enum" ? ", values" : ""}
            </summary>
            <div className="mt-3 space-y-3 rounded-md border border-gray-100 bg-gray-50 p-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Validation regex (optional)"
                  placeholder="^MHL-\d{6}$"
                  value={slot.validationRegex ?? ""}
                  onChange={(e) => update(i, { validationRegex: e.target.value })}
                  hint="Pattern the value must match after normalization."
                />
                <div>
                  <label className="block text-sm font-medium text-gray-900">Normalizer</label>
                  <select
                    value={slot.normalizer ?? ""}
                    onChange={(e) => update(i, { normalizer: e.target.value || undefined })}
                    className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    {NORMALIZERS.map((n) => (
                      <option key={n.value} value={n.value}>{n.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {slot.type === "enum" && (
                <Input
                  label="Allowed values"
                  placeholder="urgent, high, normal, low"
                  value={(slot.enumValues ?? []).join(", ")}
                  onChange={(e) =>
                    update(i, {
                      enumValues: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                    })
                  }
                  hint="Comma-separated list. Minimum 2 values."
                />
              )}
            </div>
          </details>
        </div>
      ))}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button type="button" size="sm" variant="secondary" onClick={add}>
          <Plus className="h-3.5 w-3.5" /> Add slot
        </Button>
      </div>
    </div>
  );
}
